import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { AbstractCommand } from './index';
import { KaraokeTrackManager } from '../lib/KaraokeTrackManager';

import type { Element } from '../models';
import type { VideoTranscriptElement } from '../models/elements/VideoTranscriptElement';
import type { VideoEditor } from '../VideoEditor';

/**
 * Command to shift multiple elements by a time offset
 */
export class ShiftMultipleElementsCommand extends AbstractCommand {
  private elementIds: string[];

  getName(): string {
    return 'ShiftMultipleElementsCommand';
  }
  private timeShift: number;
  private executed: boolean = false;
  private transcriptTrackModified: boolean = false;

  constructor(editor: VideoEditor, elementIds: string[], timeShift: number) {
    super(editor, undefined);
    this.elementIds = elementIds;
    this.timeShift = timeShift;
  }

  execute(): boolean {
    VideoEditorLogger.logCommand(this, 'start', {
      elementIds: this.elementIds,
      timeShift: this.timeShift,
      timelineMode: this.getTimelineMode(),
    });

    try {
      this.storeAffectedElements();
    } catch (error) {
      return false;
    }

    if (!this.shiftAffectedElements()) {
      return false;
    }

    this.recalculateTimeline();
    this.executed = true;

    // Reconcile karaoke track if transcript elements were shifted
    if (this.transcriptTrackModified) {
      const karaokeTrack = this.editor.getKaraokeTrack();
      if (karaokeTrack) {
        const karaokeTrackManager = new KaraokeTrackManager(this.editor);
        karaokeTrackManager.reconcileKaraokeTrack();
      }
    }

    VideoEditorLogger.logCommand(this, 'complete', {
      elementIds: this.elementIds,
      timeShift: this.timeShift,
    });

    return true;
  }

  /**
   * Checks if the element is a VideoTranscriptElement
   */
  private isTranscriptTrackElement(
    element: Element,
  ): element is VideoTranscriptElement {
    return (
      this.editor
        .getTranscriptTrack()
        ?.elements?.some(
          (el): el is VideoTranscriptElement => el.id === element.id,
        ) ?? false
    );
  }

  private shiftAffectedElements(): boolean {
    for (const affectedElement of this.affectedElements) {
      const { element } = affectedElement;
      const newStartTime = element.time + this.timeShift;
      let success;
      if (this.isTranscriptTrackElement(element)) {
        this.transcriptTrackModified = true;
        success = this.editor
          .getTranscriptTrackManager()
          .shiftElement(element, newStartTime, this.editor.getTimelineMode());
      } else {
        success = this.editor
          .getTrackManager()
          .shiftElement(element, newStartTime, this.editor.getTimelineMode());
      }

      if (!success) {
        VideoEditorLogger.logCommand(this, 'error', {
          error: 'Failed to shift element',
          elementId: element.id,
          timeShift: this.timeShift,
        });
        return false;
      }
    }
    return true;
  }

  undo(): boolean {
    if (!this.executed) {
      return false;
    }

    this.restoreAffectedElements();
    this.recalculateTimeline();
    return true;
  }

  getDescription(): string {
    return `Shift multiple elements (${
      this.elementIds.length
    }) by ${this.timeShift.toFixed(2)}s`;
  }

  protected storeAffectedElements(): void {
    this.affectedElements = this.elementIds
      .map(id => {
        const element = this.editor.getElementById(id);
        if (!element) {
          VideoEditorLogger.logCommand(this, 'error', {
            error: 'Failed to store affected elements: element not found',
            elementId: id,
            timeShift: this.timeShift,
          });
          throw new Error(`Element with ID ${id} not found`);
        }
        return element;
      })
      .map(element => ({
        element,
        originalState: this.captureElementState(element),
      }));
    // Need to ensure the order of operation, so that there are no conflicts:
    // e.g. first element cannot be shifted to the right because the second
    // affected element is in the way. To prevent such situation we should move
    // the rightmost element first (and vice versa for left direction).
    if (this.timeShift > 0) {
      this.affectedElements.sort((a, b) => b.element.time - a.element.time);
    } else {
      this.affectedElements.sort((a, b) => a.element.time - b.element.time);
    }
  }

  protected restoreAffectedElements(): void {
    this.affectedElements.forEach(({ element, originalState }) => {
      this.restoreElementState(element, originalState);
    });
  }
}
